import type { ScmAuthApi } from '@backstage/integration-react';
import { OPEN_PULL_REQUESTS_QUERY } from '../githubOrg';
import { githubGraphqlPartial } from './githubGraphql';
import { DAY_MS, ageMs, timeAgo } from './time';
import { NeedsYouItem } from './types';

/** One search for everything the card needs to say whose move it is. */
const QUERY = `query MyPullRequests($q: String!) {
  search(query: $q, type: ISSUE, first: 50) {
    nodes {
      ... on PullRequest {
        id
        number
        title
        url
        isDraft
        createdAt
        updatedAt
        repository { nameWithOwner }
        reviewDecision
        mergeable
        reviewRequests(first: 1) { totalCount }
        commits(last: 1) { nodes { commit { statusCheckRollup { state } } } }
      }
    }
  }
}`;

export type MyPullRequest = {
  id: string;
  number: number;
  title: string;
  url: string;
  isDraft: boolean;
  createdAt: string;
  updatedAt: string;
  repository: { nameWithOwner: string };
  // The fields below can come back null when the portal's GitHub App token
  // may not read them (see githubGraphql), so treat null as "unknown".
  /** Also null when the repository needs no reviews. */
  reviewDecision: 'APPROVED' | 'CHANGES_REQUESTED' | 'REVIEW_REQUIRED' | null;
  /** GitHub computes this lazily and answers UNKNOWN until it has. */
  mergeable: 'MERGEABLE' | 'CONFLICTING' | 'UNKNOWN' | null;
  reviewRequests: { totalCount: number } | null;
  commits: {
    nodes:
      | ({
          commit: {
            statusCheckRollup: {
              state: 'SUCCESS' | 'FAILURE' | 'ERROR' | 'PENDING' | 'EXPECTED';
            } | null;
          } | null;
        } | null)[]
      | null;
  } | null;
  /** Set here, not by GitHub: the token may not read this pull request's
   * check results, so its checks are unknown rather than absent. */
  checksUnknown?: boolean;
};

/**
 * The signed-in user's open pull requests with their review, merge and check
 * state, from GitHub's GraphQL API with the user's own GitHub token. Roadie's
 * search returns none of these states, nor real dates.
 */
export const fetchMyPullRequests = async (
  scmAuth: ScmAuthApi,
): Promise<MyPullRequest[]> => {
  const { data, errors } = await githubGraphqlPartial<{
    search: { nodes: (MyPullRequest | null)[] };
  }>(scmAuth, QUERY, { q: OPEN_PULL_REQUESTS_QUERY });
  // An error on a node's check results means the token may not read them:
  // mark that pull request, so "no checks" and "unreadable" stay apart.
  const unreadableChecks = new Set(
    errors.flatMap(error =>
      error.path?.[0] === 'search' &&
      error.path[1] === 'nodes' &&
      error.path.includes('statusCheckRollup')
        ? [error.path[2]]
        : [],
    ),
  );
  return data.search.nodes.flatMap((pr, index) =>
    // A pull request GitHub couldn't return at all comes back as null.
    pr ? [{ ...pr, checksUnknown: unreadableChecks.has(index) }] : [],
  );
};

/** No commits, pushes, reviews or comments for this long: stale. */
export const STALE_DAYS = 14;
/** A pull request with reviewers waits this long before it shows. */
const WAITING_DAYS = 2;

const days = (ms: number) => Math.floor(ms / DAY_MS);

/**
 * Whose move a pull request is, as a Needs you row, or nothing when it's
 * someone else's move and not overdue. The first matching reason wins:
 * conflicts, failing checks, changes requested and a ready merge are the
 * author's move now; then a pull request nobody touched in two weeks, one
 * with no reviewer, and one waiting for reviewers for days. Drafts only show
 * once stale.
 */
export const pullRequestItem = (
  pr: MyPullRequest,
  now = Date.now(),
): NeedsYouItem | undefined => {
  const checks = pr.commits?.nodes?.[0]?.commit?.statusCheckRollup?.state;
  const idle = ageMs(pr.updatedAt, now);
  const open = ageMs(pr.createdAt, now);
  const item = (
    priority: NeedsYouItem['priority'],
    urgency: NeedsYouItem['urgency'],
    label: string,
    meta: string,
    since: string,
  ): NeedsYouItem => ({
    id: `github-mine-${pr.id}`,
    source: 'github-my-prs',
    priority,
    urgency,
    label,
    meta: `${pr.repository.nameWithOwner} #${pr.number} · ${meta}`,
    title: pr.title,
    action: { label: 'Open', href: pr.url, external: true },
    since: new Date(since).getTime(),
  });
  const updated = `updated ${timeAgo(pr.updatedAt, now)}`;

  if (pr.isDraft) {
    return idle >= STALE_DAYS * DAY_MS
      ? item(
          'background',
          'low',
          'Stale draft',
          `no activity for ${days(idle)} days · finish or close it`,
          pr.updatedAt,
        )
      : undefined;
  }
  if (pr.mergeable === 'CONFLICTING') {
    return item(
      'your-move',
      'warning',
      'Merge conflicts',
      updated,
      pr.updatedAt,
    );
  }
  if (checks === 'FAILURE' || checks === 'ERROR') {
    return item(
      'your-move',
      'warning',
      'Checks failing',
      updated,
      pr.updatedAt,
    );
  }
  if (pr.reviewDecision === 'CHANGES_REQUESTED') {
    return item(
      'your-move',
      'warning',
      'Changes requested',
      updated,
      pr.updatedAt,
    );
  }
  if (pr.reviewDecision === 'APPROVED' && pr.mergeable === 'MERGEABLE') {
    // Checks we can't read might be failing: don't call it ready.
    if (pr.checksUnknown) {
      return item(
        'wrap-up',
        'normal',
        'Approved',
        "approved · check results aren't visible here",
        pr.updatedAt,
      );
    }
    // Good news rather than an alarm: finish it when you can. No check
    // result here means the repository has no checks.
    if (checks === undefined || checks === 'SUCCESS') {
      return item(
        'wrap-up',
        'normal',
        'Ready to merge',
        checks === 'SUCCESS' ? 'approved, checks pass' : 'approved, no checks',
        pr.updatedAt,
      );
    }
  }
  if (idle >= STALE_DAYS * DAY_MS) {
    return item(
      'wrap-up',
      'normal',
      'No activity',
      `for ${days(idle)} days · nudge, finish or close it`,
      pr.updatedAt,
    );
  }
  if (
    pr.reviewDecision === 'REVIEW_REQUIRED' &&
    pr.reviewRequests?.totalCount === 0
  ) {
    return item(
      'your-move',
      'normal',
      'No reviewer',
      `opened ${timeAgo(pr.createdAt, now)} · ask for a review`,
      pr.createdAt,
    );
  }
  if (
    pr.reviewDecision === 'REVIEW_REQUIRED' &&
    open >= WAITING_DAYS * DAY_MS
  ) {
    return item(
      'background',
      'low',
      'Waiting for review',
      `opened ${timeAgo(pr.createdAt, now)}`,
      pr.createdAt,
    );
  }
  return undefined;
};
